import * as React from "react";
import {Breadcrumb, Button, ControlLabel, FormControl, FormGroup} from "react-bootstrap";
import {defineMessages, FormattedMessage, InjectedIntlProps, injectIntl} from "react-intl";
import {ControlRoomTable} from "../table/ControlRoomTable";
import {WithApi, WithApiProperties} from "../../util/WithApi";
import {DirectoryItem} from "../../../model";
import {DirectoryItemsColumnFactory} from "./DirectoryItemsColumnFactory";
import {ChooseFileStrategy} from "./strategies/ChooseFileStrategy";
import {ChooseLocalFileStrategy} from "./strategies/ChooseLocalFileStrategy";
import {ChooseLocalOrNetworkFileStrategy} from "./strategies/ChooseLocalOrNetworkFileStrategy";
import {Logger} from "../../util/Logger";
import {Alert, Checkbox} from "react-bootstrap/lib";
import {Autocomplete} from "@material-ui/lab";
import * as mui from "@material-ui/core";
import {TextField} from "@material-ui/core";
import {mapErrorToMessage} from "../../util/ErrorMapper";
import {ErrorDisplay} from "../errordisplay/ErrorDisplay";
import {ADD_DATA_INITIAL_FOLDER_KEY, localRepository} from "../../LocalRepository";
import {getInitialPath} from "../../util/Util";

export enum ChooseFileFormSourceType {
  Local,
  LocalAndNetwork,
  S3,
}

export enum ChooseMode {
  FOLDERS,
  FILES,
}

export enum ItemType {
  FOLDER,
  FILE,
}

export interface ChooseFileFormItem {
  path: string;
  type: ItemType;
}

interface ChooseFileFormState {
  directoryPath: string;                      // Path of the current directory
  directoryItems: DirectoryItem[];            // Items in the current directory
  selectedItems: DirectoryItem[];
  nextLocation: string;
  editingLocation: boolean;
  fileCandidates: ChooseFileFormItem[];
  locationValidation: "error" | null;
  isAllDataLoaded: boolean;
  fileFiltersActive: string[];
  fileFiltersEnabled: boolean;
  clearSelection: boolean;
  error: Error;
}

interface ChooseFileFormBaseProps {
  source?: ChooseFileFormSourceType;
  chooseMode: ChooseMode;
  fileFilters?: string[];
  fileFiltersActive?: string[];
  fileFiltersEnabled?: boolean;
  initialPath?: string;
  buttonText: string;
  canMultiSelect: boolean;
  handleChoose?: SingleHandleChoose;
  handleMultiChoose?: MultiHandleChoose;
}

export type SingleHandleChoose = (filePath: string, fileType: ItemType) => Promise<any>;

export type MultiHandleChoose = (files: ChooseFileFormItem[]) => Promise<any>;

type ChooseFileFormProps = ChooseFileFormBaseProps & WithApiProperties & InjectedIntlProps;

const getDirectoryItemsListDimensions = (parent: HTMLElement) => {
  const paddingLeft = parseFloat(getComputedStyle(parent).paddingLeft) || 0;
  const paddingRight = parseFloat(getComputedStyle(parent).paddingRight) || 0;
  return {
    width: parent.clientWidth - paddingLeft - paddingRight,
    height: 400,
  };
};

const MESSAGES = defineMessages({
  activeFileFilters: {
    id: "studio.add-data-dialog.active-add-filters",
    defaultMessage: "Selected file extensions",
  }
});

const DirectoryItemsListComponent = ControlRoomTable(new DirectoryItemsColumnFactory(),
    getDirectoryItemsListDimensions);

class ChooseFileFormComponent extends React.Component<ChooseFileFormProps, ChooseFileFormState> {
  _logger: Logger = Logger.getLogger("ChooseFileForm");

  private readonly strategy: ChooseFileStrategy;

  constructor(props: ChooseFileFormProps) {
    super(props);

    this.strategy = this.createStrategy();
    this.state = {
      directoryPath: this.strategy.pathSeparator,
      directoryItems: [],
      selectedItems: [],
      nextLocation: "",
      editingLocation: false,
      fileCandidates: ChooseMode.FILES ? [] : [{
        path: this.strategy.pathSeparator,
        type: ItemType.FOLDER,
      }],
      locationValidation: null,
      isAllDataLoaded: true,
      fileFiltersActive: this.props.fileFiltersActive ? this.props.fileFiltersActive : [],
      fileFiltersEnabled: this.props.fileFiltersEnabled ? this.props.fileFiltersEnabled : false,
      clearSelection: false,
      error: null,
    };
  }

  private createStrategy() {
    const {source, api} = this.props;

    switch (source) {
    case ChooseFileFormSourceType.LocalAndNetwork:
      return new ChooseLocalOrNetworkFileStrategy(api);
    case ChooseFileFormSourceType.Local:
    default:
      return new ChooseLocalFileStrategy(api);
    }
  }

  componentDidMount() {
    const {initialPath, api} = this.props;
    const pathSeparator = this.strategy.pathSeparator;
    if (initialPath) {
      if (this.props.chooseMode === ChooseMode.FILES && initialPath.lastIndexOf(pathSeparator) > 0) {
        this.changeDirectory(initialPath.slice(0, initialPath.lastIndexOf(pathSeparator) + 1));
      } else {
        this.changeDirectory(initialPath);
      }
    } else {
      getInitialPath(api).then((savedInitialPath) => {
        this.changeDirectory(savedInitialPath);
      })
    }
  }

  changeDirectory = (directoryPath: string) => {
    this.setState({isAllDataLoaded: false, clearSelection: false});
    this._logger.debug('changeDirectory = (directoryPath: string) => {')
    this.strategy.browse(directoryPath).then((directoryItems) => {
      this._logger.debug('this.strategy.browse(directoryPath).then((directoryItems) => {')
      this.setState({isAllDataLoaded: true, clearSelection: false});
      directoryItems = this.filterFileCandidates(directoryItems, true, {directoryPath});
      directoryItems.sort((a, b) => {
        const upperA = a.fileName.toUpperCase();
        const upperB = b.fileName.toUpperCase();

        if (upperA === upperB) {
          return 0;
        }
        return (upperA > upperB ? 1 : -1);
      });

      directoryItems.sort((a, b) => {
        if (a.directory === b.directory) {
          return 0;
        }
        return a.directory ? -1 : 1;
      });

      if (this.props.chooseMode === ChooseMode.FILES) {
        this.setState(prevState => ({
          directoryItems,
          directoryPath,
          selectedItems: directoryPath === prevState.directoryPath ? prevState.selectedItems : [],
          fileCandidates: directoryPath === prevState.directoryPath ? prevState.fileCandidates : [],
          locationValidation: null,
          editingLocation: false,
          clearSelection: false,
          error: null
        }));
      } else {
        this.setState({
          directoryItems,
          directoryPath,
          selectedItems: [],
          fileCandidates: [{
            path: directoryPath,
            type: ItemType.FOLDER,
          }],
          locationValidation: null,
          editingLocation: false,
          clearSelection: false,
          error: null
        });
      }
      localRepository.setOptions(ADD_DATA_INITIAL_FOLDER_KEY, directoryPath);
    }, (error) => {
      this._logger.debug('about to: this.setState({fileCandidatesValidation: "error"})');
      this.setState({
        locationValidation: "error",
        isAllDataLoaded: true,
        editingLocation: true,
        clearSelection: false,
        error
      });
    });
  }

  generateBreadCrumb = () => {
    const breadCrumbElementsArray = this.state.directoryPath.split(/[\\|/]/);

    const breadCrumbElements = [];
    for (const element of breadCrumbElementsArray) {
      let breadCrumbElement = null;
      if (element && element.trim() !== "") {
        breadCrumbElement = (<Button bsStyle="link" bsSize="small"
                                     onClick={() => {
                                       this.handleBreadCrumbClick(element);
                                     }}>{element}</Button>);
        breadCrumbElements.push((<Breadcrumb.Item key={element} active> {breadCrumbElement} </Breadcrumb.Item>));
      }
    }

    return (
        <Breadcrumb>
          {breadCrumbElements}
        </Breadcrumb>
    );
  }

  private renderError() {
    const {error} = this.state;

    if (error) {
      const errorMessage = mapErrorToMessage(error);
      if (errorMessage) {
        return <Alert bsStyle="danger">{errorMessage}</Alert>;
      }
      return <ErrorDisplay error={error}/>;
    }
    return null;
  }

  private get firstFileCandidate() {
    return this.state.fileCandidates[0]?.path || "";
  }

  private get firstFileCandidateType() {
    return this.state.fileCandidates[0]?.type || ItemType.FOLDER;
  }

  handleBreadCrumbClick = (elementToNavigateTo) => {
    const pathSeparator = this.strategy.pathSeparator;
    const index = this.state.directoryPath.indexOf(elementToNavigateTo);
    if (index >= 0) {
      const newFolder = this.state.directoryPath.substring(0, index + elementToNavigateTo.length);
      if (/^[A-Za-z]:$/.test(newFolder)) {
        this.changeDirectory(newFolder + pathSeparator);
      } else {
        this.changeDirectory(newFolder);
      }
    } else {
      // Go to root if directory is not found
      this.changeDirectory(pathSeparator);
    }
    this.setState({clearSelection: true});
  }

  handleDoubleClick = (_event: React.MouseEvent<any>, index: number) => {
    const directoryItem = this.state.directoryItems[index];
    if (directoryItem.directory) {
      this.changeDirectory(directoryItem.absolutePath);
    }
  }

  handleRowSelect = (selectedItems: DirectoryItem[]) => {
    if (this.props.chooseMode === ChooseMode.FOLDERS) {
      this.setState({selectedItems, clearSelection: false});
      return;
    }

    const fileCandidates = this.filterFileCandidates(selectedItems, false);
    this.setState({
      selectedItems,
      fileCandidates: fileCandidates.map(c => ({
        path: c.absolutePath,
        type: c.directory ? ItemType.FOLDER : ItemType.FILE,
      })),
      locationValidation: null,
      clearSelection: false,
      error: null
    });
  }

  filterFileCandidates = (selectedItems: DirectoryItem[], includeDirectories: boolean,
                          changingState?: any): DirectoryItem[] => {
    const fileFiltersActive = changingState?.fileFiltersActive ?? this.state.fileFiltersActive;
    const fileFiltersEnabled = (typeof (changingState?.fileFiltersEnabled) !== 'undefined') ?
                               changingState.fileFiltersEnabled : this.state.fileFiltersEnabled;
    const directoryPath = changingState?.directoryPath;
    const fileCandidates = this.findFolderCandidates(selectedItems);
    const selectedFileCandidates: DirectoryItem[] = [];

    if (includeDirectories) {
      selectedFileCandidates.push(...fileCandidates.filter((fileCandidate) => (
          fileCandidate.directory && (directoryPath && (fileCandidate.absolutePath.startsWith(directoryPath)))))
      );
    }
    if (fileCandidates.length > 0 && fileFiltersEnabled) {
      fileFiltersActive.forEach((fileFilter) => {
        if (/^[A-Za-z]+$/.test(fileFilter)) {
          selectedFileCandidates.push(
              ...fileCandidates.filter(
                  (fileCandidate) => fileCandidate?.fileName.endsWith(fileFilter)));
        } else {
          const filters = /^(\(?.*\))(.*)/.exec(fileFilter);
          let re: RegExp = null;
          if (filters?.length === 3) {
            const flags = filters[1].replace(/[^gimu]+/g, '');
            re = new RegExp(filters[2], flags);
          } else {
            re = new RegExp(fileFilter, 'i');
          }
          selectedFileCandidates.push(...fileCandidates.filter((fileCandidate) => re.test(fileCandidate.fileName)));
        }
      })
    } else {
      selectedFileCandidates.push(...fileCandidates.filter((fileCandidate) => !fileCandidate.directory));
    }

    return selectedFileCandidates;
  }

  findFolderCandidates = (selectedItems: DirectoryItem[]): DirectoryItem[] => {
    const contentRootCandidate = this.state.directoryPath; // Use current directory path if no items selected

    if (selectedItems?.length > 0) {
      return selectedItems;
    }

    return [{
              absolutePath: contentRootCandidate,
              directory: true,
              fileName: contentRootCandidate
            } as DirectoryItem];
  }

  handleChoose = () => {
    if (this.props.canMultiSelect) {
      this.props.handleMultiChoose(this.state.fileCandidates);
    } else {
      this.props.handleChoose(this.firstFileCandidate, this.firstFileCandidateType);
    }
  }

  handleLocationInputChanged = (e) => {
    this.setState({
      nextLocation: e.target.value,
      editingLocation: true,
      clearSelection: false
    });
  }

  handleLocationInputEnterPressed = (e) => {
    if (e.keyCode === 13) {
      this.changeDirectory(e.target.value);
      e.preventDefault();
      this.setState({
        editingLocation: false,
        clearSelection: false
      });
    }
  }

  selectedRowIndices = (): number[] => {
    const rowIndices = [];
    const {directoryItems, selectedItems} = this.state;
    selectedItems.forEach((selectedItem: DirectoryItem) => {
      const index = directoryItems.findIndex(obj => obj.absolutePath === selectedItem.absolutePath);
      if (index !== -1) {
        rowIndices.push(index);
      }
    })
    return rowIndices;
  }

  render() {
    const {buttonText, intl, canMultiSelect, fileFilters} = this.props;
    const {fileFiltersEnabled} = this.state;
    this._logger.debug('001 render: this.formatLocation()')
    return (
        <div>
          {this.renderError()}
          <form>
            <FormGroup controlId="location" validationState={this.state.locationValidation}>
              {this.generateBreadCrumb()}
              <ControlLabel>
                <FormattedMessage id="studio.settings.add-data-dialog.location" defaultMessage="Location"/>
              </ControlLabel>
              <input
                  className="form-control"
                  type="text"
                  value={this.state.editingLocation ? this.state.nextLocation : this.state.directoryPath}
                  onChange={this.handleLocationInputChanged}
                  onKeyDown={this.handleLocationInputEnterPressed}
              />
            </FormGroup>
          </form>
          <DirectoryItemsListComponent isPaged={false}
                                       clearSelection={this.state.clearSelection}
                                       intl={intl}
                                       data={this.state.directoryItems}
                                       selectedRowIndices={this.selectedRowIndices()}
                                       isAllDataLoaded={this.state.isAllDataLoaded}
                                       loadData={() => undefined}
                                       onRowDoubleClick={this.handleDoubleClick}
                                       onRowSelect={this.handleRowSelect}
                                       canMultiSelect={canMultiSelect}
                                       noItemsFoundMessage={<FormattedMessage
                                           id="studio.settings.add-root-form.empty-folder"
                                           defaultMessage="Empty directory"/>}
          />
          {(this.props.chooseMode === ChooseMode.FILES) && fileFilters && <div>
            <Checkbox data-cy="add-only-selected-extensions" name="fileFiltersEnabled"
                      checked={fileFiltersEnabled} onChange={this.onChange}>
              <FormattedMessage id="studio.add-data-dialog.add-selected"
                                defaultMessage="Add only selected extensions"/>
            </Checkbox>
            {this.state.fileFiltersEnabled && <Autocomplete
                data-cy="extensions"
                multiple
                options={fileFilters}
                value={this.state.fileFiltersActive}
                disableCloseOnSelect
                onChange={(_event, value) => this.onChange({target: {name: "fileFiltersActive", value}})}
                renderOption={(option, {selected}) => (
                    <React.Fragment>
                      <mui.Checkbox checked={selected}/>
                      {option}
                    </React.Fragment>
                )}
                renderInput={(params) => (
                    <TextField {...params} variant="outlined"
                               placeholder={intl.formatMessage(MESSAGES.activeFileFilters)}/>
                )}
            />}
          </div>}
          <div className="chooseFileForm-selection">
            <ControlLabel>
              <FormattedMessage id="studio.settings.add-data-dialog.selection" defaultMessage="Selection"/>
            </ControlLabel>
            <FormControl
                type="text"
                value={this.formatSelection()}
                disabled={true}
            />
          </div>
          <Button onClick={this.handleChoose} block
                  disabled={this.state.fileCandidates.length === 0}>
            {buttonText}</Button>
        </div>
    );
  }

  private readonly onChange = (event: any, forcedValue?: any) => {
    const {name} = event.target;
    let {value} = event.target;
    value = event.target.type === 'checkbox' ? event.target.checked : value;
    value = forcedValue !== undefined ? forcedValue : value;
    this.setState(prevState => {
      const fileCandidates = this.filterFileCandidates(prevState.selectedItems, false, {[name]: value} as any);
      return {
        [name]: value,
        fileCandidates: fileCandidates.map(c => ({
          path: c.absolutePath,
          type: c.directory ? ItemType.FOLDER : ItemType.FILE,
          clearSelection: true
        }))
      } as any
    });
    this.changeDirectory(this.state.directoryPath);
  }

  private formatSelection() {
    this._logger.debug('002 private formatLocation() {   and   this.state.fileCandidates: ' +
                       this.state.fileCandidates.map(f => f.path).join("; "));
    return this.state.fileCandidates.map(f => f.path).join("; ");
  }
}

export const ChooseFileForm = injectIntl(WithApi(ChooseFileFormComponent));